/**
 * @file StatementPdfPane.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-20
 * @modified 2026-10-20
 *
 * One statement's PDF drawn in place (PdfCanvasPreview, no browser tab), with
 * the run's Contents applied — so what is approved is what goes (Cam,
 * 20 Oct 2026: "previewing should reflect what will actually send/print").
 * An Email / Print switch shows either layout.
 *
 * 1.1.0 (2026-10-20, the statements look): the switch is the screen's
 * segmented control; `notice` (a line under the head — Review's "doesn't
 * match Xero") and `headExtra` (the drawer's close button) added.
 * 1.2.0 (2026-10-20, Cam — statement notes): `noteKey` (utils/statementNotes
 * previewNoteKey of the customer note) is part of the preview's key, so a note
 * just saved is on the next preview in both layouts, without regenerating —
 * the server reads the note from the statement as it is now.
 */

import React from 'react';
import PdfCanvasPreview from './PdfCanvasPreview';
import { fetchStatementPreview } from './StatementPreviewModal';

/**
 * @param {object} props
 * @param {number|null} props.statementId
 * @param {string} [props.noteKey] - changes when the customer note does (the preview is drawn again)
 * @param {object} props.content - { include, includeAllUnpaidInvoices, suppressOverdueStamp, listUsedCredits, attachUsedCredits }
 * @param {'email'|'print'} props.template
 * @param {function} props.onTemplate
 * @param {React.ReactNode} [props.heading]
 * @param {React.ReactNode} [props.notice]
 * @param {React.ReactNode} [props.headExtra]
 * @param {React.ReactNode} [props.actions]
 */
function StatementPdfPane({ statementId, noteKey = '', content, template, onTemplate, heading = null, notice = null, headExtra = null, actions = null, emptyText = 'Choose a statement to preview it.' }) {
  const key = statementId
    ? `stmt:${statementId}:n${noteKey}:${template}:${content.include}:${content.includeAllUnpaidInvoices ? 1 : 0}:${content.suppressOverdueStamp ? 1 : 0}:${content.listUsedCredits ? 1 : 0}:${content.attachUsedCredits ? 1 : 0}`
    : null;
  return (
    <div className="sr-pdf-pane">
      <div className="sr-pdf-head">
        <div className="sr-pdf-heading">{heading}</div>
        <div className="sr-seg" role="radiogroup" aria-label="Layout">
          {['email', 'print'].map(t => (
            <button key={t} type="button" role="radio" aria-checked={template === t}
              className={template === t ? 'on' : ''} onClick={() => onTemplate(t)}>
              <span className="material-symbols-rounded">{t === 'email' ? 'mail' : 'print'}</span>
              {t === 'email' ? 'Email layout' : 'Print layout'}
            </button>
          ))}
        </div>
        {headExtra}
      </div>
      {notice}
      <div className="sr-pdf-pages">
        {statementId ? (
          <PdfCanvasPreview
            key={key}
            className="sr-pdf-canvas"
            loadKey={key}
            scale={1.25}
            load={() => fetchStatementPreview(statementId, { template, ...content })}
          />
        ) : (
          <div className="sr-empty">
            <span className="sr-empty-icon"><span className="material-symbols-rounded">description</span></span>
            <p>{emptyText}</p>
          </div>
        )}
      </div>
      {actions && <div className="sr-pdf-actions">{actions}</div>}
    </div>
  );
}

export default StatementPdfPane;
